import StateObserver from 'src/StateObserver';
import { getReturnUrl, getWebPurchaseDTO } from 'src/lib/iap/iap.util';
import { useCache, withLoading } from 'src/lib/utils';
import { ProductId } from 'src/replicant/iap';
import {
  RedirectStateAnalytics,
  RevenueAnalytics,
} from '../analytics/analytics.types';
import NakedPromise from 'src/game/minigame/match2-timestep/lib/pattern/NakedPromise';
import { getPaymentChildWindow } from '../iap.utils';
import { Feature } from 'src/replicant/analytics';
import {
  getRevenueBasePrice,
  getRevenueGrossAnalytics,
} from '../analytics/iap.gross.revenue';
import type { PendingPayment } from 'src/replicant/iap/iap.pending.payments';
import { KomojuPaymentMethod, PurchaseCompleteResult } from '../iap.types';
import { PurchaseProviderCreditCardBrand } from '@play-co/replicant';

// https://docs.komoju.com/en/multipay/overview/#integrating
interface KomojuMultipayPopupOptions {
  amount: number;
  currency: string;
  endpoint?: string;
  locale?: string;
  title?: string;
  description?: string;
  methods?: string[];
  panelLabel?: string;
  image?: string;
  prefillEmail?: string;
  hideCreditCardBrands?: string[];
}

// This is an undocumented API that has been reverse engineered
// from the Komoju SDK. It is not guaranteed to be stable.
interface KomojuWidget {
  close(): void;
}

interface KomojuMultipayHandler {
  iframe: KomojuWidget;

  open(options: KomojuMultipayPopupOptions): void;
}

interface KomojuMultipay {
  configure(params: {
    key: string;
    token: (token: { id: string }) => void;
  }): KomojuMultipayHandler;
}

interface Komoju {
  multipay: KomojuMultipay;
}

declare global {
  interface Window {
    Komoju: Komoju;
  }
}

let hostedFieldsSdkCache: Promise<void>;

/**
 * Load komoju hosted fields SDK
 *
 * https://doc.komoju.com/docs/fields-overview
 */
export async function loadKomojuHostedFieldsSDK(): Promise<void> {
  return (hostedFieldsSdkCache ??= import(
    'https://multipay.komoju.com/fields.js' as any
  ));
}

/**
 * Calculates the revenue analytics for a given product based on the payment method and credit card brand.
 *
 * @param productId - The ID of the product for which the revenue is being calculated.
 * @param paymentMethod - The payment method used for the purchase.
 * @param creditCardBrand - The brand of the credit card used for the purchase, if applicable.
 * @returns The revenue analytics for the specified product and payment method.
 */
export function getKomojuRevenue(
  productId: ProductId,
  paymentMethod: KomojuPaymentMethod,
  creditCardBrand: PurchaseProviderCreditCardBrand | undefined,
): RevenueAnalytics {
  // Revenue analytics
  return getRevenueGrossAnalytics(productId, {
    ...getRevenueBasePrice(productId),
    provider: 'komoju',
    providerMethod: paymentMethod,
    providerCreditCardBrand: creditCardBrand,
  });
}

/**
 * Finish komoju purchase flow
 */
export async function komojuFinishPurchaseSequence(
  paymentToken: string,
  productId: ProductId,
  analytics: { feature: string; subFeature: string },
  webPayAnalytics: RedirectStateAnalytics,
  methodTimestamp: number,
): Promise<PurchaseCompleteResult> {
  // Create komoju payment based on token
  const {
    redirectUrl,
    paymentMethod,
    creditCardBrand,
    pendingPayment,
    purchaseID,
  } = await withLoading(
    async () =>
      StateObserver.invoke.asyncCreateKomojuPayment({
        paymentToken,
        productId,
        // In case we have a child window we use a different URL, to avoid the auth flow from starting before we have the chance to close the child window.
        returnUrl: getPaymentChildWindow()
          ? 'https://buruburu-payment-confirm.playco.games/'
          : getReturnUrl(),
        analytics,
        webPayAnalytics,
        methodTimestamp,
      }),
    {
      feature: Feature.KOMOJU._,
      subFeature: Feature.KOMOJU.CREATE_PAYMENT,
    },
  );

  // Revenue analytics
  const revenue: RevenueAnalytics = {
    ...getKomojuRevenue(productId, paymentMethod, creditCardBrand),
    purchaseProviderCreditCardBrand: creditCardBrand,
  };

  // Handle redirect URL if present.
  if (redirectUrl && pendingPayment) {
    // Try handling it in a child window.
    const capturedProduct = await openKomojuWindow(redirectUrl, pendingPayment);

    // We have captured products, handle them.
    if (capturedProduct && capturedProduct.productId === productId) {
      // Continue purchasing flow
      return {
        response: getWebPurchaseDTO(productId),
        revenue,
        purchaseID,
      };
    }

    // If it was not possible to open a child window, redirect instead.
    // Flush replicant before redirecting.
    await withLoading(async () => StateObserver.flush(), {
      feature: Feature.KOMOJU._,
      subFeature: Feature.KOMOJU.REPLICANT_FLUSH,
    });

    // Returning a redirect URL will cause the client to redirect.
    return {
      response: { redirectUrl },
      revenue,
      purchaseID,
    };
  }

  // Purchase was handled in the same window, close the child window if it exists.
  getPaymentChildWindow()?.close();

  // Payment completed, get purchase info
  return {
    response: getWebPurchaseDTO(productId),
    revenue,
    purchaseID,
  };
}

/**
 * Create payment session
 */
export async function komojuCreateSession(
  productId: ProductId,
): Promise<string> {
  // If we are in offline mode, return a debug session ID.
  if (process.env.IS_LOCAL_DEVELOPMENT === 'true') {
    return process.env.KOMOJU_DEBUG_SESSION ?? '';
  }

  return StateObserver.invoke
    .asyncCreateKomojuSession({
      productId,
      returnUrl: getReturnUrl(),
    })
    .then((result) => result.id);
}

/**
 * Get pending session
 */
export function getKomojuSessionCache(productId: ProductId) {
  return useCache(
    () => komojuCreateSession(productId),
    `komojuSession-${productId}`,
  );
}

/**
 * Opens a Komoju payment window and handles the payment process.
 *
 * @param redirectUrl - The URL to redirect the child window to for payment processing.
 * @returns A promise that resolves to an array of captured products if the payment is successful,
 * or null if the child window was blocked. If the payment is cancelled, the promise is rejected
 * with an error object containing the code 'paymentCancelled'.
 */
async function openKomojuWindow(
  redirectUrl: string,
  pendingPayment: PendingPayment,
): Promise<PendingPayment | null> {
  // Open popup window.
  const child = getPaymentChildWindow();

  // Child was blocked, redirect instead.
  if (!child) {
    return null;
  }

  // Child exists, set its location to the redirect URL.
  if (child) {
    child.location.assign(redirectUrl);
  }

  // Poll payment status, resolve promise when payment is completed and return captured product.
  const capturedProduct = await pollKomojuStatus(child, pendingPayment);

  // If no captured product, payment was cancelled.
  if (capturedProduct === null) {
    return Promise.reject({
      code: 'USER_INPUT',
      message: 'No captured payments',
    });
  }

  // Payment was successful return captured purchase
  return capturedProduct;
}

/**
 * Polls the status of a Komoju payment by periodically checking if pending payment was captured.
 * Resolves the promise with the captured payment or null if the payment was cancelled or some other error occurred.
 *
 * @param {Window} child - The child window used for the payment process.
 * @param {PendingPayment} pendingPayment - Pending payment to poll.
 * @returns {Promise<PendingPayment | null>} - A promise that resolves with the captured payments.
 */
async function pollKomojuStatus(
  child: Window,
  pendingPayment: PendingPayment,
): Promise<PendingPayment | null> {
  const resultPromise = new NakedPromise<PendingPayment | null>();
  let timeout: ReturnType<typeof setTimeout>;
  let visibilityHandler: () => void;

  // Resolve promise with result.
  const resolve = (capturedPayment: PendingPayment | null) => {
    // stop polling/throttle
    clearTimeout(timeout);
    // Remove event listener.
    document.removeEventListener('visibilitychange', visibilityHandler);
    // resolve overall promise
    resultPromise.resolve(capturedPayment);
    // Close child window.
    child?.close();
  };

  // Check payment status. Returns true if the status was handled. Otherwise false.
  const checkStatus = async (finalCheck: boolean): Promise<boolean> => {
    // Check if payment was captured
    const status =
      await StateObserver.replicant.asyncGetters.getPendingPaymentStatus({
        pendingPaymentId: pendingPayment.id,
      });

    // If we have captured payments, we can resolve the promise.
    if (status === 'captured') {
      const result =
        await StateObserver.invoke.fulfillPendingPayment(pendingPayment);
      resolve(result);
      return true;
    }

    // Check for case where payment no in state in some reason
    if (status === undefined) {
      resolve(null);
      return true;
    }

    // Lets wait for payment to be captured
    if (status === 'pending') {
      if (finalCheck) {
        // If this is the final check, resolve the promise with null anyway (leave payment pending and exit polling)
        resolve(null);
      }
      return false; // Wait more for payment to be captured, it will continue polling
    }

    // There is unclear pending status like canceled, failed etc.
    // Exit from loop no sense wait it more
    resolve(null);
    return true;
  };

  // Wrapper around checkStatus that returns the same promise until it resolves.
  let checkStatusPromise: NakedPromise<boolean> | null = null;
  let isFinalized = false;
  const checkStatusSingletonPromise = async (finalCheck: boolean) => {
    isFinalized = finalCheck;

    // If there is a checkStatusPromise, return it.
    if (checkStatusPromise) {
      return checkStatusPromise;
    }
    // Otherwise, create a new checkStatusPromise.
    checkStatusPromise = new NakedPromise<boolean>();
    // Check the status.
    const result = await checkStatus(finalCheck);
    // Resolve the checkStatusPromise.
    checkStatusPromise.resolve(result);
    checkStatusPromise = null;

    // if the final check already occurred, exit polling
    return isFinalized || result;
  };

  // Check payment status if user returns to the page.
  // In case of failure, throttle a final check in 5s.
  visibilityHandler = async () => {
    // First time - stops long polling (tab switch to payment)
    // Following calls - throttles final statusCheck if the user switches tabs multiple times
    clearTimeout(timeout);

    // If the document is visible, check the payment.
    if (document.visibilityState === 'visible') {
      // Do a check immedeately to see if the payment was captured.
      const result = await checkStatusSingletonPromise(false);
      // If payment was not captured, do a final check again in 5 seconds and exit
      if (!result) {
        // eslint-disable-next-line require-atomic-updates
        timeout = setTimeout(() => {
          void checkStatusSingletonPromise(true); // final check & exit
        }, 5000);
      }
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  // doc is still visible (tab didn't need to switch), let's poll until document becomes hidden or payment is captured
  // this should not happen in normal flow, but it's a safety net
  if (document.visibilityState === 'visible') {
    // Poll payment status on interval.
    const poll = async () => {
      const result = await checkStatusSingletonPromise(false);
      if (!result) {
        timeout = setTimeout(poll, 1000);
      }
    };
    // start polling
    void poll();
  }

  return resultPromise;
}

/**
 * We can provite a custom theme for the Komoju fields.
 * @see https://doc.komoju.com/docs/styling-your-fields
 */
const komojuHotStyle = `
.komoju-spinner {
  margin: auto !important;
}`;

export const komojuTheme = `data:text/css;base64,${btoa(komojuHotStyle)}`;

type KomojuFields = Element & {
  submit: () => Promise<{
    created_at: string;
    id: string;
    payment_details: { type: string };
    resource: string;
  }>;
  initPromise: Promise<void>;
};

export function getKomojuHostedFields(): KomojuFields {
  const existing = document.querySelector('komoju-fields');
  if (existing) {
    return existing as KomojuFields;
  }

  const fields = document.createElement(
    'komoju-fields',
  ) as unknown as KomojuFields;

  // Set the required attributes.
  fields.id = 'web-pay-komoju-fields';
  fields.setAttribute('publishable-key', process.env.KOMOJU_PUBLISHABLE_KEY!);
  fields.setAttribute('komoju-api', 'https://komoju.com');
  fields.setAttribute('locale', StateObserver.getLocale());
  fields.setAttribute('theme', komojuTheme);
  fields.setAttribute('style', `display: none;`);
  // Most popular payment method. Having a default value is important so the iframe is loaded as soon as a sessionID is set.
  fields.setAttribute('payment-type', 'paypay');
  // This will force the `token` mode.
  fields.setAttribute('token', '');

  // Create a promise that resolves when the session ID is set and the field becomes ready.
  fields.initPromise = new Promise<void>((resolve) => {
    const handler = () => {
      fields.removeEventListener('komoju-session-change', handler);
      resolve();
    };

    fields.addEventListener('komoju-session-change', handler);
  });

  return fields;
}
